import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import './style.css';
import { hasVariableVelocity, PRESETS, QUALITY, type Preset, type QualityName } from './presets';
import { Simulation } from './Simulation';
import {
  VelocitySonifier, type AudioMode, type AudioPalette, type DistanceTimbre, type VelocityTimbre
} from './VelocitySonifier';
import { XRPanel, type PanelAction } from './XRPanel';

const canvas = document.querySelector<HTMLCanvasElement>('#scene');
if (!canvas) throw new Error('Scene canvas is missing.');
const recordingSupported = typeof MediaRecorder !== 'undefined' && typeof canvas.captureStream === 'function';

// An alpha-capable XR framebuffer is required for immersive-ar passthrough.
// Ordinary immersive-vr sessions remain opaque because the scene background
// and clear alpha are restored to their existing values for that mode.
const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', alpha: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.setSize(innerWidth, innerHeight);
renderer.setClearColor(0x02030a, 1);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.xr.enabled = true;
renderer.xr.setReferenceSpaceType('local-floor');

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x02030a);
const virtualFog = new THREE.FogExp2(0x060918, 0.055);
scene.fog = virtualFog;
const query = new URLSearchParams(location.search);
const xrDebugMode = query.has('xrdebug');
const xrStaticMode = query.has('xrstatic');
const particleOverride = Number(query.get('particles')) || undefined;
const VELOCITY_TIMBRES: VelocityTimbre[] = ['softPad', 'brightPad', 'deepSpace', 'choir', 'shimmer', 'glass', 'darkDrone', 'sine'];
const DISTANCE_TIMBRES: DistanceTimbre[] = ['warm', 'choir', 'shimmer', 'deep', 'sine'];
const camera = new THREE.PerspectiveCamera(58, innerWidth / innerHeight, 0.01, 10000);
camera.position.set(0, 1.55, 3.1);
const playerRig = new THREE.Group();
playerRig.name = 'player-rig';
playerRig.add(camera);
scene.add(playerRig);

const controls = new OrbitControls(camera, canvas);
controls.target.set(0, 1.55, -2.6);
controls.enableDamping = true;
controls.dampingFactor = 0.05;
controls.minDistance = 1;
controls.maxDistance = 1000;

let quality = (localStorage.getItem('gspace-quality') as QualityName) || 'performance';
if (!(quality in QUALITY)) quality = 'balanced';
const storedPresetIndex = ((Number(localStorage.getItem('gspace-preset') ?? 1) % PRESETS.length) + PRESETS.length) % PRESETS.length;
const presetPairOrder = localStorage.getItem('gspace-preset-order') === 'paired';
// v59 changes the list from all originals followed by all tuned copies to
// adjacent original/tuned pairs. Translate the old saved index exactly once.
let presetIndex = presetPairOrder
  ? storedPresetIndex
  : storedPresetIndex < PRESETS.length / 2
    ? storedPresetIndex * 2
    : (storedPresetIndex - PRESETS.length / 2) * 2 + 1;
if (!presetPairOrder) {
  localStorage.setItem('gspace-preset', String(presetIndex));
  localStorage.setItem('gspace-preset-order', 'paired');
}
let velocityColourScale = PRESETS[presetIndex].colourScale;
let particlePointScale = quality === 'performance' ? 8 : 10;
let animationSpeed = 1;
let structureScale = THREE.MathUtils.clamp(Number(localStorage.getItem('gspace-structure-scale')) || 1, 0.5, 40);
let distantBrightness = THREE.MathUtils.clamp(Number(localStorage.getItem('gspace-distant-brightness')) || 1, 1, 6);
let tracerTrailsEnabled = localStorage.getItem('gspace-tracer-trails') === 'true';
let gravityMarkersEnabled = localStorage.getItem('gspace-gravity-markers') === 'true';
let passthroughEnabled = localStorage.getItem('gspace-passthrough') === 'true';
let immersiveVrSupported = false;
let immersiveArSupported = false;
let passthroughSessionActive = false;
let audioEnabled = localStorage.getItem('gspace-audio-enabled') === 'true';
let velocityAudioEnabled = localStorage.getItem('gspace-velocity-audio-enabled') !== 'false';
let audioMode: AudioMode = localStorage.getItem('gspace-audio-mode') === 'scientific' ? 'scientific' : 'musical';
const storedVelocityTimbre = localStorage.getItem('gspace-velocity-timbre');
let velocityTimbre: VelocityTimbre = VELOCITY_TIMBRES.includes(storedVelocityTimbre as VelocityTimbre)
  ? storedVelocityTimbre as VelocityTimbre : 'softPad';
const storedDistanceTimbre = localStorage.getItem('gspace-distance-timbre');
let distanceTimbre: DistanceTimbre = DISTANCE_TIMBRES.includes(storedDistanceTimbre as DistanceTimbre)
  ? storedDistanceTimbre as DistanceTimbre : 'warm';
const storedAudioVolume = Number(localStorage.getItem('gspace-audio-volume'));
let audioVolume = localStorage.getItem('gspace-audio-volume') !== null && Number.isFinite(storedAudioVolume)
  ? THREE.MathUtils.clamp(storedAudioVolume, 0, 1)
  : 0.3;
const storedAudioRange = Number(localStorage.getItem('gspace-audio-range'));
let audioRange = Number.isFinite(storedAudioRange) && storedAudioRange > 0
  ? THREE.MathUtils.clamp(storedAudioRange, 1, 100)
  : velocityColourScale;
const storedAudioResponse = Number(localStorage.getItem('gspace-audio-response'));
let audioResponse = Number.isFinite(storedAudioResponse) && storedAudioResponse > 0
  ? THREE.MathUtils.clamp(storedAudioResponse, 0.05, 2)
  : 0.45;
const storedMusicalMovement = Number(localStorage.getItem('gspace-musical-movement'));
let musicalMovement = localStorage.getItem('gspace-musical-movement') !== null && Number.isFinite(storedMusicalMovement)
  ? THREE.MathUtils.clamp(storedMusicalMovement, 0, 1)
  : 0.55;
const storedEventDensity = Number(localStorage.getItem('gspace-event-density'));
let eventDensity = localStorage.getItem('gspace-event-density') !== null && Number.isFinite(storedEventDensity)
  ? THREE.MathUtils.clamp(storedEventDensity, 0, 1) : 0.65;
let adaptiveAudioRanges = localStorage.getItem('gspace-adaptive-audio-ranges') !== 'false';
let rareAccentsEnabled = localStorage.getItem('gspace-rare-accents') !== 'false';
let spatialSourcesEnabled = localStorage.getItem('gspace-spatial-sources') !== 'false';
let distanceAudioEnabled = localStorage.getItem('gspace-distance-audio-enabled') !== 'false';
const storedDistanceAudioMix = Number(localStorage.getItem('gspace-distance-audio-mix'));
let distanceAudioMix = localStorage.getItem('gspace-distance-audio-mix') !== null && Number.isFinite(storedDistanceAudioMix)
  ? THREE.MathUtils.clamp(storedDistanceAudioMix, 0, 1)
  : 0.45;
const storedDistanceAudioRange = Number(localStorage.getItem('gspace-distance-audio-range'));
let distanceAudioRange = Number.isFinite(storedDistanceAudioRange) && storedDistanceAudioRange > 0
  ? THREE.MathUtils.clamp(storedDistanceAudioRange, 10, 5000)
  : 1000;
let cullEnabled = true;
let cullDistance = presetCullDistance(presetIndex);
let audioPalette: AudioPalette = paletteForPreset(PRESETS[presetIndex]);
const storedParticleGrid = Number(localStorage.getItem('gspace-particle-grid'));
let particleGridSize = clampParticleGrid(
  particleOverride ?? (Number.isFinite(storedParticleGrid) && storedParticleGrid > 0 ? storedParticleGrid : QUALITY[quality].size)
);
let simulation: Simulation;
try {
  simulation = new Simulation(renderer, PRESETS[presetIndex], quality, particleGridSize);
  simulation.setStructureScale(structureScale);
  simulation.setDistantBrightness(distantBrightness);
  simulation.setTracerTrails(tracerTrailsEnabled);
  simulation.setGravityMarkers(gravityMarkersEnabled);
  simulation.root.position.x = 0.75;
  scene.add(simulation.root);
} catch (error) {
  showFatal(error instanceof Error ? error.message : String(error));
  throw error;
}

const sonifier = new VelocitySonifier();
sonifier.setMode(audioMode);
sonifier.setVelocityTimbre(velocityTimbre);
sonifier.setDistanceTimbre(distanceTimbre);
sonifier.setPalette(audioPalette);
sonifier.setVolume(audioVolume);
sonifier.setResponse(audioResponse);
sonifier.setMusicalMovement(musicalMovement);
sonifier.setEventDensity(eventDensity);
sonifier.setRareAccentsEnabled(rareAccentsEnabled);
sonifier.setSpatialSourcesEnabled(spatialSourcesEnabled);
sonifier.setVelocityEnabled(velocityAudioEnabled);
sonifier.setDistanceEnabled(distanceAudioEnabled);
sonifier.setDistanceMix(distanceAudioMix);

const ambient = new THREE.HemisphereLight(0x88bfff, 0x060612, 0.35);
scene.add(ambient);

// A deliberately simple, shader-free XR reference scene. It is enabled by
// ?xrdebug=1 or automatically after a simulation exception.
const diagnosticGroup = new THREE.Group();
const diagnosticCube = new THREE.Mesh(
  new THREE.BoxGeometry(0.55, 0.55, 0.55),
  new THREE.MeshBasicMaterial({ color: 0x00e5ff })
);
diagnosticCube.position.set(0, 1.55, -2);
diagnosticGroup.add(diagnosticCube);
const diagnosticRing = new THREE.Mesh(
  new THREE.TorusGeometry(0.75, 0.035, 12, 64),
  new THREE.MeshBasicMaterial({ color: 0xff4fd8 })
);
diagnosticRing.position.set(0, 1.55, -2.05);
diagnosticGroup.add(diagnosticRing);
const diagnosticFloor = new THREE.GridHelper(10, 20, 0x36d7ff, 0x274060);
diagnosticGroup.add(diagnosticFloor);
diagnosticGroup.visible = xrDebugMode;
scene.add(diagnosticGroup);
if (xrDebugMode) {
  simulation.root.visible = false;
  scene.background = new THREE.Color(0x101c38);
} else if (xrStaticMode) {
  scene.background = new THREE.Color(0x101c38);
}

const panel = new XRPanel(handlePanelAction);
scene.add(panel);
panel.visible = false;

const recordingCamera = new THREE.PerspectiveCamera(70, 16 / 9, camera.near, camera.far);
const recordingHeadPosition = new THREE.Vector3();
const recordingEyePosition = new THREE.Vector3();
const recordingCameraPosition = new THREE.Vector3();
const recordingCameraScale = new THREE.Vector3();
const recordingHeadOrientation = new THREE.Quaternion();
let mediaRecorder: MediaRecorder | null = null;
let recordingVideoStream: MediaStream | null = null;
let recordingFinalizing = false;
let recordingStartedAt = 0;
let recordingElapsedSeconds = 0;
let recordingNotice = '';
let recordingObjectUrl: string | null = null;
let lastRecordingMirrorTime = -Infinity;
let lastRecordingUiSecond = -1;

const raycaster = new THREE.Raycaster();
const tempMatrix = new THREE.Matrix4();
const locomotionViewDirection = new THREE.Vector3();
const locomotionHorizontalForward = new THREE.Vector3();
const locomotionRight = new THREE.Vector3();
const worldUp = new THREE.Vector3(0, 1, 0);
const audioHeadPosition = new THREE.Vector3();
const audioViewDirection = new THREE.Vector3();
const audioRight = new THREE.Vector3();
const audioSourceDirection = new THREE.Vector3();
const controllers: THREE.Group[] = [];
const controllerRays: THREE.Line[] = [];
let heldPanelController: THREE.Group | null = null;
let panelNeedsPlacement = false;
for (let i = 0; i < 2; i++) {
  const controller = renderer.xr.getController(i);
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]);
  const line = new THREE.Line(lineGeometry, new THREE.LineBasicMaterial({ color: 0x70d8ff, transparent: true, opacity: 0.72 }));
  line.scale.z = 4;
  line.visible = false;
  controller.add(line);
  controllerRays.push(line);
  const handle = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.025, 0.09, 4, 8),
    new THREE.MeshBasicMaterial({ color: 0x243556 })
  );
  handle.rotation.x = Math.PI * 0.2;
  handle.position.set(0, -0.055, 0.035);
  controller.add(handle);
  controller.addEventListener('selectstart', () => {
    controller.userData.triggerHeld = !selectFromController(controller);
  });
  controller.addEventListener('selectend', () => { controller.userData.triggerHeld = false; });
  controller.addEventListener('squeezestart', () => grabPanel(controller));
  controller.addEventListener('squeezeend', () => releasePanel(controller));
  controller.addEventListener('connected', (event) => {
    const source = event.data as XRInputSource;
    controller.userData.gamepad = source.gamepad;
    controller.userData.handedness = source.handedness;
  });
  controller.addEventListener('disconnected', () => {
    releasePanel(controller);
    delete controller.userData.gamepad;
    delete controller.userData.handedness;
    delete controller.userData.triggerHeld;
  });
  playerRig.add(controller);
  controllers.push(controller);
}

const landing = required<HTMLElement>('#landing');
const enterButton = required<HTMLButtonElement>('#enter-xr');
const statusElement = required<HTMLElement>('#xr-status');
const presetSelect = required<HTMLSelectElement>('#preset');
const qualitySelect = required<HTMLSelectElement>('#quality');
const particleGridInput = required<HTMLInputElement>('#particle-grid');
const particleCountOutput = required<HTMLOutputElement>('#particle-count');
const colourScaleInput = required<HTMLInputElement>('#colour-scale');
const colourScaleOutput = required<HTMLOutputElement>('#colour-scale-value');
const pointScaleInput = required<HTMLInputElement>('#point-scale');
const pointScaleOutput = required<HTMLOutputElement>('#point-scale-value');
const animationSpeedInput = required<HTMLInputElement>('#animation-speed');
const animationSpeedOutput = required<HTMLOutputElement>('#animation-speed-value');
const structureScaleInput = required<HTMLInputElement>('#structure-scale');
const structureScaleOutput = required<HTMLOutputElement>('#structure-scale-value');
const distantBrightnessInput = required<HTMLInputElement>('#distant-brightness');
const distantBrightnessOutput = required<HTMLOutputElement>('#distant-brightness-value');
const passthroughInput = required<HTMLInputElement>('#passthrough');
const passthroughOutput = required<HTMLOutputElement>('#passthrough-value');
const recordVideoButton = required<HTMLButtonElement>('#record-video');
const recordingStatus = required<HTMLElement>('#recording-status');
const recordingDownload = required<HTMLAnchorElement>('#recording-download');
const tracerTrailsInput = required<HTMLInputElement>('#tracer-trails');
const tracerTrailsOutput = required<HTMLOutputElement>('#tracer-trails-value');
const gravityMarkersInput = required<HTMLInputElement>('#gravity-markers');
const gravityMarkersOutput = required<HTMLOutputElement>('#gravity-markers-value');
const audioEnabledInput = required<HTMLInputElement>('#audio-enabled');
const audioEnabledOutput = required<HTMLOutputElement>('#audio-enabled-value');
const velocityAudioEnabledInput = required<HTMLInputElement>('#velocity-audio-enabled');
const velocityAudioEnabledOutput = required<HTMLOutputElement>('#velocity-audio-enabled-value');
const audioModeSelect = required<HTMLSelectElement>('#audio-mode');
const velocityTimbreSelect = required<HTMLSelectElement>('#velocity-timbre');
const distanceTimbreSelect = required<HTMLSelectElement>('#distance-timbre');
const audioPaletteOutput = required<HTMLOutputElement>('#audio-palette-value');
const audioVolumeInput = required<HTMLInputElement>('#audio-volume');
const audioVolumeOutput = required<HTMLOutputElement>('#audio-volume-value');
const audioRangeInput = required<HTMLInputElement>('#audio-range');
const audioRangeOutput = required<HTMLOutputElement>('#audio-range-value');
const audioResponseInput = required<HTMLInputElement>('#audio-response');
const audioResponseOutput = required<HTMLOutputElement>('#audio-response-value');
const musicalMovementInput = required<HTMLInputElement>('#musical-movement');
const musicalMovementOutput = required<HTMLOutputElement>('#musical-movement-value');
const eventDensityInput = required<HTMLInputElement>('#event-density');
const eventDensityOutput = required<HTMLOutputElement>('#event-density-value');
const adaptiveAudioRangesInput = required<HTMLInputElement>('#adaptive-audio-ranges');
const adaptiveAudioRangesOutput = required<HTMLOutputElement>('#adaptive-audio-ranges-value');
const rareAccentsInput = required<HTMLInputElement>('#rare-accents');
const rareAccentsOutput = required<HTMLOutputElement>('#rare-accents-value');
const spatialSourcesInput = required<HTMLInputElement>('#spatial-sources');
const spatialSourcesOutput = required<HTMLOutputElement>('#spatial-sources-value');
const distanceAudioEnabledInput = required<HTMLInputElement>('#distance-audio-enabled');
const distanceAudioEnabledOutput = required<HTMLOutputElement>('#distance-audio-enabled-value');
const distanceAudioMixInput = required<HTMLInputElement>('#distance-audio-mix');
const distanceAudioMixOutput = required<HTMLOutputElement>('#distance-audio-mix-value');
const distanceAudioRangeInput = required<HTMLInputElement>('#distance-audio-range');
const distanceAudioRangeOutput = required<HTMLOutputElement>('#distance-audio-range-value');
const cullEnabledInput = required<HTMLInputElement>('#cull-enabled');
const cullEnabledOutput = required<HTMLOutputElement>('#cull-enabled-value');
const cullDistanceInput = required<HTMLInputElement>('#cull-distance');
const cullDistanceOutput = required<HTMLOutputElement>('#cull-distance-value');
const fpsElement = required<HTMLElement>('#fps');
const pauseButton = required<HTMLButtonElement>('#pause');

PRESETS.forEach((preset, index) => presetSelect.add(new Option(
  `${presetCode(index)} · ${preset.name}${hasVariableVelocity(preset) ? ' · VARIABLE VELOCITY' : ''}${preset.legacyMotionTuned ? ' · LEGACY-MOTION TUNED' : ''}`,
  String(index)
)));
presetSelect.value = String(presetIndex);
qualitySelect.value = quality;
updateUi();

enterButton.addEventListener('click', () => void enterImmersive());
required<HTMLButtonElement>('#reset-view').addEventListener('click', resetView);
required<HTMLButtonElement>('#previous').addEventListener('click', () => selectPreset(presetIndex - 1));
required<HTMLButtonElement>('#next').addEventListener('click', () => selectPreset(presetIndex + 1));
pauseButton.addEventListener('click', togglePause);
required<HTMLButtonElement>('#reset').addEventListener('click', restart);
presetSelect.addEventListener('change', () => selectPreset(Number(presetSelect.value)));
qualitySelect.addEventListener('change', () => setQuality(qualitySelect.value as QualityName));
particleGridInput.addEventListener('input', () => {
  particleCountOutput.textContent = formatParticleCount(Number(particleGridInput.value));
});
particleGridInput.addEventListener('change', () => setParticleGrid(Number(particleGridInput.value)));
colourScaleInput.addEventListener('input', () => setVelocityColourScale(Number(colourScaleInput.value)));
pointScaleInput.addEventListener('input', () => setParticlePointScale(Number(pointScaleInput.value)));
animationSpeedInput.addEventListener('input', () => setAnimationSpeed(Number(animationSpeedInput.value)));
structureScaleInput.addEventListener('input', () => setStructureScale(structureScaleFromFraction(Number(structureScaleInput.value))));
distantBrightnessInput.addEventListener('input', () => setDistantBrightness(Number(distantBrightnessInput.value)));
passthroughInput.addEventListener('change', () => setPassthroughEnabled(passthroughInput.checked));
recordVideoButton.addEventListener('click', () => toggleVideoRecording());
tracerTrailsInput.addEventListener('change', () => setTracerTrails(tracerTrailsInput.checked));
gravityMarkersInput.addEventListener('change', () => setGravityMarkers(gravityMarkersInput.checked));
audioEnabledInput.addEventListener('change', () => setAudioEnabled(audioEnabledInput.checked));
velocityAudioEnabledInput.addEventListener('change', () => setVelocityAudioEnabled(velocityAudioEnabledInput.checked));
audioModeSelect.addEventListener('change', () => setAudioMode(audioModeSelect.value as AudioMode));
velocityTimbreSelect.addEventListener('change', () => setVelocityTimbre(velocityTimbreSelect.value as VelocityTimbre));
distanceTimbreSelect.addEventListener('change', () => setDistanceTimbre(distanceTimbreSelect.value as DistanceTimbre));
audioVolumeInput.addEventListener('input', () => setAudioVolume(Number(audioVolumeInput.value) / 100));
audioRangeInput.addEventListener('input', () => setAudioRange(Number(audioRangeInput.value)));
audioResponseInput.addEventListener('input', () => setAudioResponse(Number(audioResponseInput.value)));
musicalMovementInput.addEventListener('input', () => setMusicalMovement(Number(musicalMovementInput.value) / 100));
eventDensityInput.addEventListener('input', () => setEventDensity(Number(eventDensityInput.value) / 100));
adaptiveAudioRangesInput.addEventListener('change', () => setAdaptiveAudioRanges(adaptiveAudioRangesInput.checked));
rareAccentsInput.addEventListener('change', () => setRareAccents(rareAccentsInput.checked));
spatialSourcesInput.addEventListener('change', () => setSpatialSources(spatialSourcesInput.checked));
distanceAudioEnabledInput.addEventListener('change', () => setDistanceAudioEnabled(distanceAudioEnabledInput.checked));
distanceAudioMixInput.addEventListener('input', () => setDistanceAudioMix(Number(distanceAudioMixInput.value) / 100));
distanceAudioRangeInput.addEventListener('input', () => setDistanceAudioRange(distanceRangeFromFraction(Number(distanceAudioRangeInput.value))));
cullEnabledInput.addEventListener('change', () => setCullEnabled(cullEnabledInput.checked));
cullDistanceInput.addEventListener('input', () => setCullDistance(Number(cullDistanceInput.value)));

window.addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight);
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    simulation.paused = true;
    sonifier.suspendForBackground();
  } else if (audioEnabled) {
    void sonifier.setEnabled(true).catch(console.warn);
  }
  updateUi();
});
// Browsers only permit audio startup from a user gesture. This also restores a
// previously enabled audio setting when the user next taps the page.
document.addEventListener('pointerdown', () => {
  if (audioEnabled && !sonifier.isEnabled) void sonifier.setEnabled(true).catch(console.warn);
}, { capture: true });

let simulationStepCredit = 0;
let frames = 0;
let fpsTime = performance.now();
let menuButtonWasDown = false;
let pauseButtonWasDown = false;
let stepButtonWasDown = false;
let lowFpsSamples = 0;
let simulationFailed = false;
let previousFrameTime = performance.now();
let lastAudioSampleTime = -Infinity;
let lastAudioSpatialTime = -Infinity;
let lastAdaptiveAudioUiTime = -Infinity;
renderer.setAnimationLoop((time) => {
  const deltaSeconds = Math.min((time - previousFrameTime) / 1000, 0.05);
  previousFrameTime = time;
  const stepInterval = 1000 / QUALITY[quality].simulationHz;
  simulationStepCredit = Math.min(simulationStepCredit + deltaSeconds * 1000 / stepInterval * animationSpeed, 20);
  const scheduledSteps = Math.floor(simulationStepCredit);
  if (!xrDebugMode && !xrStaticMode && !simulationFailed && scheduledSteps > 0) {
    try {
      simulation.step(scheduledSteps);
    } catch (error) {
      simulationFailed = true;
      simulation.paused = true;
      simulation.root.visible = false;
      diagnosticGroup.visible = true;
      applySessionBackground(passthroughSessionActive);
      console.error('GSpace simulation pass failed; showing XR reference scene.', error);
      toast('Simulation paused after a GPU error');
    }
    simulationStepCredit -= scheduledSteps;
  }
  diagnosticCube.rotation.x = time * 0.00035;
  diagnosticCube.rotation.y = time * 0.00055;
  if (!renderer.xr.isPresenting) controls.update();
  else {
    updateLocomotion(deltaSeconds);
    pollControllerButtons();
    if (panelNeedsPlacement) placePanelInFront();
  }
  if (audioEnabled && sonifier.isEnabled && time - lastAudioSampleTime >= 125) {
    try {
      const histograms = simulation.sampleAudioHistograms(audioRange, distanceAudioRange);
      adaptAudioRangesFromHistograms(histograms.velocity, histograms.distance, time);
      sonifier.update(histograms.velocity, histograms.distance);
      lastAudioSampleTime = time;
    } catch (error) {
      console.warn('Velocity audio sampling failed; audio has been disabled.', error);
      setAudioEnabled(false);
      toast('Audio disabled after a GPU sampling error');
    }
  }
  if (audioEnabled && spatialSourcesEnabled && time - lastAudioSpatialTime >= 250) {
    updateAudioSpatialization();
    lastAudioSpatialTime = time;
  }
  renderer.render(scene, camera);
  if (isVideoRecording()) {
    if (renderer.xr.isPresenting && !passthroughSessionActive && time - lastRecordingMirrorTime >= 1000 / 24) {
      renderRecordingMirror();
      lastRecordingMirrorTime = time;
    }
    const elapsedSecond = Math.floor((performance.now() - recordingStartedAt) / 1000);
    if (elapsedSecond !== lastRecordingUiSecond) {
      recordingElapsedSeconds = elapsedSecond;
      lastRecordingUiSecond = elapsedSecond;
      updateUi();
    }
  }
  frames++;
  if (time - fpsTime >= 1000) {
    const fps = Math.round(frames * 1000 / (time - fpsTime));
    fpsElement.textContent = `${fps} FPS`;
    adaptQuality(fps);
    frames = 0;
    fpsTime = time;
  }
});

void detectXr();
if ('serviceWorker' in navigator) {
  const developmentHost = location.hostname === 'localhost'
    || location.hostname === '127.0.0.1'
    || /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(location.hostname)
    || location.hostname.endsWith('.trycloudflare.com');
  if (developmentHost) {
    // Temporary origins change frequently; retaining a worker here can pin an
    // obsolete XR bundle even after the tunnel starts serving a new build.
    void navigator.serviceWorker.getRegistrations().then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister()))
    );
    if ('caches' in window) {
      void caches.keys().then((keys) => Promise.all(keys.filter((key) => key.startsWith('gspace-')).map((key) => caches.delete(key))));
    }
  } else if (import.meta.env.PROD && location.protocol === 'https:') {
    window.addEventListener('load', () => navigator.serviceWorker.register('./service-worker.js').catch(console.warn));
  }
}

async function detectXr(): Promise<void> {
  if (!navigator.xr) {
    enterButton.disabled = true;
    passthroughInput.disabled = true;
    passthroughOutput.textContent = 'Unavailable';
    statusElement.textContent = 'Immersive XR is unavailable in this browser. Desktop mode is active.';
    return;
  }
  [immersiveVrSupported, immersiveArSupported] = await Promise.all([
    sessionSupported('immersive-vr'),
    sessionSupported('immersive-ar')
  ]);
  if (passthroughEnabled && !immersiveArSupported) {
    passthroughEnabled = false;
    localStorage.setItem('gspace-passthrough', 'false');
  }
  updateUi();
  updateXrStatus();
  if (xrDebugMode && immersiveVrSupported) statusElement.textContent = 'XR diagnostic mode · particle simulation bypassed';
  if (xrStaticMode && immersiveVrSupported) statusElement.textContent = `XR static-particle test · ${particleGridSize}² particles`;
  if ((immersiveVrSupported || immersiveArSupported) && 'getDigitalGoodsService' in window && new URLSearchParams(location.search).get('source') === 'pwa') {
    void enterImmersive();
  }
}

async function enterImmersive(): Promise<void> {
  if (!navigator.xr || renderer.xr.isPresenting) return;
  const sessionMode: XRSessionMode = passthroughEnabled ? 'immersive-ar' : 'immersive-vr';
  const modeSupported = passthroughEnabled ? immersiveArSupported : immersiveVrSupported;
  if (!modeSupported) {
    updateXrStatus();
    return;
  }
  if (sessionMode === 'immersive-ar' && isVideoRecording()) stopVideoRecording('Canvas recording stopped for passthrough');
  enterButton.disabled = true;
  statusElement.textContent = passthroughEnabled ? 'Starting Quest passthrough…' : 'Entering immersive space…';
  try {
    // Render the XR projection layer at the runtime's native recommended size.
    // Sub-native layers can expose rectangular resolution boundaries in-headset.
    renderer.xr.setFramebufferScaleFactor(1);
    const session = await navigator.xr.requestSession(sessionMode, {
      optionalFeatures: ['local-floor', 'bounded-floor']
    });
    session.addEventListener('end', () => {
      if (isVideoRecording()) stopVideoRecording('Recording stopped when the XR session ended');
      if (heldPanelController) releasePanel(heldPanelController);
      landing.classList.remove('hidden');
      setPanelVisible(false);
      simulation.root.position.x = 0.75;
      enterButton.disabled = false;
      applySessionBackground(false);
      if (recordingNotice.startsWith('PASSTHROUGH:')) recordingNotice = '';
      updateUi();
      updateXrStatus();
      statusElement.textContent = 'Immersive session ended · Ready to return';
    });
    applySessionBackground(sessionMode === 'immersive-ar');
    await renderer.xr.setSession(session);
    simulation.root.position.x = 0;
    landing.classList.add('hidden');
    setPanelVisible(true);
    panelNeedsPlacement = true;
    renderer.xr.setFoveation(0);
    toast(sessionMode === 'immersive-ar'
      ? 'Passthrough active · trigger selects the panel'
      : 'Trigger selects · use the panel to explore');
  } catch (error) {
    applySessionBackground(false);
    enterButton.disabled = false;
    statusElement.textContent = passthroughEnabled
      ? 'Could not start passthrough. Turn it off to use immersive VR.'
      : 'Could not start VR. Tap Enter immersive to try again.';
    console.error(error);
  }
}

async function sessionSupported(mode: XRSessionMode): Promise<boolean> {
  try {
    return await navigator.xr!.isSessionSupported(mode);
  } catch (error) {
    console.warn(`Could not check ${mode} support.`, error);
    return false;
  }
}

function applySessionBackground(passthrough: boolean): void {
  passthroughSessionActive = passthrough;
  if (passthrough) {
    // The Quest compositor reveals the camera feed only through transparent
    // pixels in an immersive-ar projection layer.
    scene.background = null;
    scene.fog = null;
    renderer.setClearColor(0x000000, 0);
    return;
  }
  const diagnosticBackground = xrDebugMode || xrStaticMode || simulationFailed;
  scene.background = new THREE.Color(diagnosticBackground ? 0x101c38 : 0x02030a);
  scene.fog = virtualFog;
  renderer.setClearColor(diagnosticBackground ? 0x101c38 : 0x02030a, 1);
}

function updateXrStatus(): void {
  const requestedModeAvailable = passthroughEnabled ? immersiveArSupported : immersiveVrSupported;
  enterButton.disabled = !requestedModeAvailable;
  enterButton.textContent = passthroughEnabled ? 'Enter passthrough' : 'Enter immersive';
  if (passthroughEnabled && immersiveArSupported) {
    statusElement.textContent = 'Quest passthrough detected · Ready for mixed reality';
  } else if (immersiveVrSupported) {
    statusElement.textContent = immersiveArSupported
      ? 'Quest detected · Immersive VR and passthrough available'
      : 'Quest detected · Ready for immersive VR';
  } else {
    statusElement.textContent = 'Immersive XR is unavailable. Desktop mode is active.';
  }
}

function toggleVideoRecording(): void {
  if (passthroughSessionActive) {
    recordingNotice = 'PASSTHROUGH: OPEN QUEST CAMERA AND CHOOSE RECORD VIDEO';
    recordingStatus.textContent = 'Use Quest system Camera → Record video to include your room.';
    updateUi();
    return;
  }
  if (isVideoRecording()) {
    stopVideoRecording('Finishing video…');
    return;
  }
  if (recordingFinalizing) {
    toast('The previous video is still being prepared');
    return;
  }
  startVideoRecording();
}

function startVideoRecording(): void {
  if (!recordingSupported) {
    recordingNotice = 'VIDEO RECORDING IS UNAVAILABLE IN THIS BROWSER';
    updateUi();
    return;
  }
  try {
    if (recordingObjectUrl) {
      URL.revokeObjectURL(recordingObjectUrl);
      recordingObjectUrl = null;
    }
    recordingDownload.hidden = true;
    recordingDownload.removeAttribute('href');
    const videoStream = canvas!.captureStream(24);
    const videoTracks = videoStream.getVideoTracks();
    if (videoTracks.length === 0) throw new Error('The canvas did not provide a video track.');
    const audioTrack = sonifier.getRecordingAudioTrack();
    const captureStream = new MediaStream(audioTrack ? [...videoTracks, audioTrack] : videoTracks);
    const mimeType = preferredRecordingMimeType(Boolean(audioTrack));
    const options: MediaRecorderOptions = {
      videoBitsPerSecond: 5_000_000,
      ...(audioTrack ? { audioBitsPerSecond: 128_000 } : {}),
      ...(mimeType ? { mimeType } : {})
    };
    const recorder = new MediaRecorder(captureStream, options);
    const chunks: Blob[] = [];
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });
    recorder.addEventListener('error', (event) => {
      console.error('Video recording failed.', event);
      recordingNotice = 'VIDEO RECORDING FAILED';
      if (recorder.state !== 'inactive') recorder.stop();
      updateUi();
    });
    recorder.addEventListener('stop', () => finishVideoRecording(recorder, videoStream, chunks));
    mediaRecorder = recorder;
    recordingVideoStream = videoStream;
    recordingStartedAt = performance.now();
    recordingElapsedSeconds = 0;
    lastRecordingUiSecond = -1;
    lastRecordingMirrorTime = -Infinity;
    recordingNotice = '';
    recorder.start(1000);
    updateUi();
    toast(audioTrack ? 'Recording video with simulation audio' : 'Recording video');
  } catch (error) {
    console.error('Could not start video recording.', error);
    recordingVideoStream?.getVideoTracks().forEach((track) => track.stop());
    recordingVideoStream = null;
    mediaRecorder = null;
    recordingNotice = 'VIDEO RECORDING COULD NOT START';
    updateUi();
  }
}

function stopVideoRecording(message = 'Finishing video…'): void {
  const recorder = mediaRecorder;
  if (!recorder || recorder.state === 'inactive') return;
  recordingElapsedSeconds = Math.floor((performance.now() - recordingStartedAt) / 1000);
  recordingFinalizing = true;
  recordingNotice = message.toUpperCase();
  recorder.stop();
  updateUi();
}

function finishVideoRecording(recorder: MediaRecorder, videoStream: MediaStream, chunks: Blob[]): void {
  videoStream.getVideoTracks().forEach((track) => track.stop());
  if (mediaRecorder === recorder) mediaRecorder = null;
  if (recordingVideoStream === videoStream) recordingVideoStream = null;
  recordingFinalizing = false;
  const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
  if (blob.size === 0) {
    recordingNotice = 'NO VIDEO FRAMES WERE CAPTURED';
    updateUi();
    return;
  }
  recordingObjectUrl = URL.createObjectURL(blob);
  recordingDownload.href = recordingObjectUrl;
  recordingDownload.download = `GSpace-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
  recordingDownload.hidden = false;
  recordingNotice = renderer.xr.isPresenting
    ? 'VIDEO READY - EXIT XR TO DOWNLOAD'
    : 'VIDEO READY - SELECT DOWNLOAD';
  updateUi();
  toast('Video ready to download');
}

function preferredRecordingMimeType(withAudio: boolean): string {
  const candidates = withAudio
    ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
    : ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

function isVideoRecording(): boolean {
  return mediaRecorder?.state === 'recording' || mediaRecorder?.state === 'paused';
}

function renderRecordingMirror(): void {
  const xrCamera = renderer.xr.getCamera();
  recordingHeadPosition.set(0, 0, 0);
  if (xrCamera.cameras.length > 0) {
    for (const eye of xrCamera.cameras) {
      recordingEyePosition.setFromMatrixPosition(eye.matrixWorld);
      recordingHeadPosition.add(recordingEyePosition);
    }
    recordingHeadPosition.multiplyScalar(1 / xrCamera.cameras.length);
  } else {
    recordingHeadPosition.setFromMatrixPosition(xrCamera.matrixWorld);
  }
  xrCamera.matrixWorld.decompose(recordingCameraPosition, recordingHeadOrientation, recordingCameraScale);
  recordingCamera.position.copy(recordingHeadPosition);
  recordingCamera.quaternion.copy(recordingHeadOrientation);
  recordingCamera.aspect = canvas!.width / Math.max(canvas!.height, 1);
  const eyeCamera = xrCamera.cameras[0] ?? xrCamera;
  const tanHalfVerticalFov = 1 / Math.abs(eyeCamera.projectionMatrix.elements[5]);
  recordingCamera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(tanHalfVerticalFov));
  recordingCamera.updateProjectionMatrix();
  recordingCamera.updateMatrixWorld(true);

  const oldTarget = renderer.getRenderTarget();
  const oldXrEnabled = renderer.xr.enabled;
  renderer.xr.enabled = false;
  try {
    renderer.setRenderTarget(null);
    renderer.render(scene, recordingCamera);
  } finally {
    renderer.setRenderTarget(oldTarget);
    renderer.xr.enabled = oldXrEnabled;
  }
}

function selectFromController(controller: THREE.Group): boolean {
  if (!panel.visible) return false;
  const hit = panelIntersection(controller);
  if (!hit?.uv) return false;
  return panel.hit(hit.uv);
}

function grabPanel(controller: THREE.Group): void {
  if (!panel.visible || heldPanelController) return;
  const hit = panelIntersection(controller);
  if (!hit) return;
  scene.updateMatrixWorld(true);
  controller.attach(panel);
  heldPanelController = controller;
}

function releasePanel(controller: THREE.Group): void {
  if (heldPanelController !== controller) return;
  scene.updateMatrixWorld(true);
  scene.attach(panel);
  heldPanelController = null;
}

function panelIntersection(controller: THREE.Group): THREE.Intersection | undefined {
  tempMatrix.identity().extractRotation(controller.matrixWorld);
  raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
  raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);
  return raycaster.intersectObject(panel.target, false)[0];
}

function pollControllerButtons(): void {
  let menuDown = false;
  let pauseDown = false;
  let stepDown = false;
  for (const controller of controllers) {
    const source = controller.userData as { gamepad?: Gamepad };
    const gamepad = source.gamepad;
    if (controller.userData.handedness === 'left' && gamepad?.buttons[5]?.pressed) menuDown = true;
    if (controller.userData.handedness === 'left' && gamepad?.buttons[4]?.pressed) stepDown = true;
    if (controller.userData.handedness === 'right' && gamepad?.buttons[4]?.pressed) pauseDown = true;
  }
  if (menuDown && !menuButtonWasDown) {
    if (heldPanelController) releasePanel(heldPanelController);
    setPanelVisible(!panel.visible);
    if (panel.visible) panelNeedsPlacement = true;
  }
  menuButtonWasDown = menuDown;
  if (pauseDown && !pauseButtonWasDown) togglePause();
  pauseButtonWasDown = pauseDown;
  if (stepDown && !stepButtonWasDown && simulation.paused && !xrDebugMode && !xrStaticMode && !simulationFailed) {
    try {
      simulation.step(1, true);
      toast('Advanced one simulation frame');
    } catch (error) {
      simulationFailed = true;
      simulation.root.visible = false;
      diagnosticGroup.visible = true;
      applySessionBackground(passthroughSessionActive);
      console.error('GSpace single-step failed; showing XR reference scene.', error);
      toast('Simulation paused after a GPU error');
    }
  }
  stepButtonWasDown = stepDown;
}

function placePanelInFront(): void {
  // Resolve the current XR pose after any rig locomotion performed this frame.
  // The ArrayCamera otherwise may still expose the previous rendered transform.
  playerRig.updateMatrixWorld(true);
  renderer.xr.updateCamera(camera);
  const xrCamera = renderer.xr.getCamera();
  const headPosition = new THREE.Vector3();
  const eyePosition = new THREE.Vector3();
  const cameraPosition = new THREE.Vector3();
  const cameraScale = new THREE.Vector3();
  const headOrientation = new THREE.Quaternion();
  const forward = new THREE.Vector3();
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  // Average the eye positions to obtain the true viewer centre. This remains
  // correct after physical head movement and movement of the player rig. Read
  // matrixWorld directly: the XR eye cameras are not normal children of the
  // app camera, and asking them to update themselves discards the rig transform.
  if (xrCamera.cameras.length > 0) {
    headPosition.set(0, 0, 0);
    for (const eye of xrCamera.cameras) {
      eyePosition.setFromMatrixPosition(eye.matrixWorld);
      headPosition.add(eyePosition);
    }
    headPosition.multiplyScalar(1 / xrCamera.cameras.length);
  } else {
    headPosition.setFromMatrixPosition(xrCamera.matrixWorld);
  }
  xrCamera.matrixWorld.decompose(cameraPosition, headOrientation, cameraScale);
  forward.set(0, 0, -1).applyQuaternion(headOrientation).normalize();
  right.set(1, 0, 0).applyQuaternion(headOrientation).normalize();
  up.set(0, 1, 0).applyQuaternion(headOrientation).normalize();

  // Size the panel against the headset's current vertical field of view so
  // the taller control panel is fully readable whenever it is recalled.
  const eyeCamera = xrCamera.cameras[0] ?? xrCamera;
  const distance = 1.15;
  const tanHalfVerticalFov = 1 / Math.abs(eyeCamera.projectionMatrix.elements[5]);
  const visibleHeight = 2 * distance * tanHalfVerticalFov;
  const panelGeometryHeight = 1.987;
  const fittedScale = THREE.MathUtils.clamp(visibleHeight * 0.72 / panelGeometryHeight, 0.5, 0.82);
  panel.scale.setScalar(fittedScale);

  right.copy(forward).cross(worldUp).normalize();
  panel.position.copy(headPosition)
    .addScaledVector(forward, distance)
    .addScaledVector(right, -0.32)
    .addScaledVector(up, -0.08);
  panel.up.copy(up);
  panel.lookAt(headPosition);
  panel.updateMatrixWorld(true);
  panelNeedsPlacement = false;
}

function setPanelVisible(visible: boolean): void {
  panel.visible = visible;
  controllerRays.forEach((ray) => { ray.visible = visible; });
}

function updateLocomotion(deltaSeconds: number): void {
  let rotateYaw = 0;
  let rotatePitch = 0;
  let lateralMove = 0;
  let verticalMove = 0;
  let triggerMove = 0;
  for (const controller of controllers) {
    const gamepad = controller.userData.gamepad as Gamepad | undefined;
    if (!gamepad || gamepad.axes.length < 2) continue;
    const axisOffset = gamepad.axes.length >= 4 ? 2 : 0;
    const x = applyDeadzone(gamepad.axes[axisOffset] ?? 0);
    const y = applyDeadzone(gamepad.axes[axisOffset + 1] ?? 0);
    if (controller.userData.handedness === 'left') {
      lateralMove = x;
      verticalMove = -y;
    }
    if (controller.userData.handedness === 'right') {
      rotateYaw = x;
      rotatePitch = y;
    }
    if (controller.userData.triggerHeld === true) {
      if (controller.userData.handedness === 'right') triggerMove += 1;
      if (controller.userData.handedness === 'left') triggerMove -= 1;
    }
  }

  if (triggerMove !== 0 || lateralMove !== 0 || verticalMove !== 0) {
    const xrCamera = renderer.xr.getCamera();
    xrCamera.getWorldDirection(locomotionViewDirection).normalize();
    locomotionHorizontalForward.copy(locomotionViewDirection);
    locomotionHorizontalForward.y = 0;
    if (locomotionHorizontalForward.lengthSq() < 0.001) locomotionHorizontalForward.set(0, 0, -1);
    locomotionHorizontalForward.normalize();
    locomotionRight.copy(locomotionHorizontalForward).cross(worldUp).normalize();
    const movementSpeed = 1.35 * deltaSeconds;
    playerRig.position.addScaledVector(locomotionViewDirection, triggerMove * movementSpeed);
    playerRig.position.addScaledVector(locomotionRight, lateralMove * movementSpeed);
    playerRig.position.y += verticalMove * movementSpeed;
  }

  const rotationSpeed = 0.9 * deltaSeconds;
  if (rotateYaw !== 0) simulation.root.rotateY(-rotateYaw * rotationSpeed);
  if (rotatePitch !== 0) simulation.root.rotateX(-rotatePitch * rotationSpeed);
  if (xrDebugMode) {
    if (rotateYaw !== 0) diagnosticGroup.rotateY(-rotateYaw * rotationSpeed);
    if (rotatePitch !== 0) diagnosticGroup.rotateX(-rotatePitch * rotationSpeed);
  }
}

function applyDeadzone(value: number): number {
  const deadzone = 0.16;
  if (Math.abs(value) <= deadzone) return 0;
  return Math.sign(value) * (Math.abs(value) - deadzone) / (1 - deadzone);
}

function handlePanelAction(action: PanelAction, value?: number): void {
  if (action === 'previous') selectPreset(presetIndex - 1);
  else if (action === 'next') selectPreset(presetIndex + 1);
  else if (action === 'pause') togglePause();
  else if (action === 'reset') restart();
  else if (action === 'quality') cycleQuality();
  else if (action === 'recording') toggleVideoRecording();
  else if (action === 'particles' && value !== undefined) setParticleGrid(value);
  else if (action === 'colourScale' && value !== undefined) setVelocityColourScale(value);
  else if (action === 'pointScale' && value !== undefined) setParticlePointScale(value);
  else if (action === 'animationSpeed' && value !== undefined) setAnimationSpeed(value);
  else if (action === 'structureScale' && value !== undefined) setStructureScale(value);
  else if (action === 'distantBrightness' && value !== undefined) setDistantBrightness(value);
  else if (action === 'passthrough') setPassthroughEnabled(!passthroughEnabled);
  else if (action === 'tracerTrails') setTracerTrails(!tracerTrailsEnabled);
  else if (action === 'gravityMarkers') setGravityMarkers(!gravityMarkersEnabled);
  else if (action === 'audioEnabled') setAudioEnabled(!audioEnabled);
  else if (action === 'velocityAudioEnabled') setVelocityAudioEnabled(!velocityAudioEnabled);
  else if (action === 'audioMusical') setAudioMode('musical');
  else if (action === 'audioScientific') setAudioMode('scientific');
  else if (action === 'velocityTimbre') cycleVelocityTimbre();
  else if (action === 'distanceTimbre') cycleDistanceTimbre();
  else if (action === 'audioVolume' && value !== undefined) setAudioVolume(value);
  else if (action === 'audioRange' && value !== undefined) setAudioRange(value);
  else if (action === 'audioResponse' && value !== undefined) setAudioResponse(value);
  else if (action === 'musicalMovement' && value !== undefined) setMusicalMovement(value);
  else if (action === 'eventDensity' && value !== undefined) setEventDensity(value);
  else if (action === 'adaptiveAudioRanges') setAdaptiveAudioRanges(!adaptiveAudioRanges);
  else if (action === 'rareAccents') setRareAccents(!rareAccentsEnabled);
  else if (action === 'spatialSources') setSpatialSources(!spatialSourcesEnabled);
  else if (action === 'distanceAudioEnabled') setDistanceAudioEnabled(!distanceAudioEnabled);
  else if (action === 'distanceAudioMix' && value !== undefined) setDistanceAudioMix(value);
  else if (action === 'distanceAudioRange' && value !== undefined) setDistanceAudioRange(value);
  else if (action === 'cullEnabled') setCullEnabled(!cullEnabled);
  else if (action === 'cullDistance' && value !== undefined) setCullDistance(value);
  else if (action === 'exit') void renderer.xr.getSession()?.end();
}

function selectPreset(index: number): void {
  presetIndex = (index + PRESETS.length) % PRESETS.length;
  audioPalette = paletteForPreset(PRESETS[presetIndex]);
  sonifier.setPalette(audioPalette);
  velocityColourScale = PRESETS[presetIndex].colourScale;
  cullDistance = presetCullDistance(presetIndex);
  localStorage.setItem('gspace-preset', String(presetIndex));
  simulation.rebuild(PRESETS[presetIndex], quality, particleGridSize);
  simulation.setPointScale(particlePointScale);
  simulation.setCulling(cullEnabled, cullDistance);
  resetCameraForPreset();
  updateUi();
  toast(PRESETS[presetIndex].name);
}

function setQuality(next: QualityName): void {
  if (!(next in QUALITY) || next === quality) return;
  quality = next;
  particleGridSize = clampParticleGrid(QUALITY[quality].size);
  particlePointScale = quality === 'performance' ? 8 : 10;
  localStorage.setItem('gspace-quality', quality);
  localStorage.setItem('gspace-particle-grid', String(particleGridSize));
  simulation.rebuild(PRESETS[presetIndex], quality, particleGridSize);
  simulation.setColourScale(velocityColourScale);
  simulation.setPointScale(particlePointScale);
  simulation.setCulling(cullEnabled, cullDistance);
  updateUi();
  toast(`${QUALITY[quality].label} active`);
}

function setParticleGrid(value: number): void {
  particleGridSize = clampParticleGrid(value);
  localStorage.setItem('gspace-particle-grid', String(particleGridSize));
  simulation.rebuild(PRESETS[presetIndex], quality, particleGridSize);
  simulation.setColourScale(velocityColourScale);
  simulation.setPointScale(particlePointScale);
  simulation.setCulling(cullEnabled, cullDistance);
  updateUi();
  toast(`${formatParticleCount(particleGridSize)} particles · simulation restarted`);
}

function setVelocityColourScale(value: number): void {
  velocityColourScale = Math.round(THREE.MathUtils.clamp(value, 1, 100));
  simulation.setColourScale(velocityColourScale);
  updateUi();
}

function setParticlePointScale(value: number): void {
  particlePointScale = Math.round(THREE.MathUtils.clamp(value, 2, 20));
  simulation.setPointScale(particlePointScale);
  updateUi();
}

function setAnimationSpeed(value: number): void {
  animationSpeed = Math.round(THREE.MathUtils.clamp(value, 0.25, 20) * 4) / 4;
  updateUi();
}

function setStructureScale(value: number): void {
  structureScale = Math.round(THREE.MathUtils.clamp(value, 0.5, 40) * 4) / 4;
  localStorage.setItem('gspace-structure-scale', String(structureScale));
  simulation.setStructureScale(structureScale);
  updateUi();
}

function setDistantBrightness(value: number): void {
  distantBrightness = Math.round(THREE.MathUtils.clamp(value, 1, 6) * 4) / 4;
  localStorage.setItem('gspace-distant-brightness', String(distantBrightness));
  simulation.setDistantBrightness(distantBrightness);
  updateUi();
}

function setTracerTrails(enabled: boolean): void {
  tracerTrailsEnabled = enabled;
  localStorage.setItem('gspace-tracer-trails', String(enabled));
  simulation.setTracerTrails(enabled);
  updateUi();
}

function setGravityMarkers(enabled: boolean): void {
  gravityMarkersEnabled = enabled;
  localStorage.setItem('gspace-gravity-markers', String(enabled));
  simulation.setGravityMarkers(enabled);
  updateUi();
}

function setPassthroughEnabled(enabled: boolean): void {
  if (enabled && !immersiveArSupported) {
    passthroughInput.checked = false;
    toast('Passthrough is unavailable in this browser');
    return;
  }
  passthroughEnabled = enabled;
  localStorage.setItem('gspace-passthrough', String(enabled));
  updateUi();
  if (renderer.xr.isPresenting) {
    toast(`${enabled ? 'Passthrough' : 'Virtual background'} selected · exit and re-enter to apply`);
  } else {
    updateXrStatus();
  }
}

function setAudioEnabled(enabled: boolean): void {
  audioEnabled = enabled;
  localStorage.setItem('gspace-audio-enabled', String(enabled));
  updateUi();
  void sonifier.setEnabled(enabled).catch((error) => {
    console.warn('Could not start Web Audio.', error);
    audioEnabled = false;
    localStorage.setItem('gspace-audio-enabled', 'false');
    updateUi();
    toast('This browser could not start audio');
  });
}

function setAudioMode(mode: AudioMode): void {
  audioMode = mode === 'scientific' ? 'scientific' : 'musical';
  localStorage.setItem('gspace-audio-mode', audioMode);
  sonifier.setMode(audioMode);
  updateUi();
}

function setVelocityTimbre(timbre: VelocityTimbre): void {
  velocityTimbre = VELOCITY_TIMBRES.includes(timbre) ? timbre : 'softPad';
  localStorage.setItem('gspace-velocity-timbre', velocityTimbre);
  sonifier.setVelocityTimbre(velocityTimbre);
  updateUi();
}

function cycleVelocityTimbre(): void {
  setVelocityTimbre(VELOCITY_TIMBRES[(VELOCITY_TIMBRES.indexOf(velocityTimbre) + 1) % VELOCITY_TIMBRES.length]);
}

function setDistanceTimbre(timbre: DistanceTimbre): void {
  distanceTimbre = DISTANCE_TIMBRES.includes(timbre) ? timbre : 'warm';
  localStorage.setItem('gspace-distance-timbre', distanceTimbre);
  sonifier.setDistanceTimbre(distanceTimbre);
  updateUi();
}

function cycleDistanceTimbre(): void {
  setDistanceTimbre(DISTANCE_TIMBRES[(DISTANCE_TIMBRES.indexOf(distanceTimbre) + 1) % DISTANCE_TIMBRES.length]);
}

function setVelocityAudioEnabled(enabled: boolean): void {
  velocityAudioEnabled = enabled;
  localStorage.setItem('gspace-velocity-audio-enabled', String(enabled));
  sonifier.setVelocityEnabled(enabled);
  updateUi();
}

function setAudioVolume(value: number): void {
  audioVolume = Math.round(THREE.MathUtils.clamp(value, 0, 1) * 100) / 100;
  localStorage.setItem('gspace-audio-volume', String(audioVolume));
  sonifier.setVolume(audioVolume);
  updateUi();
}

function setAudioRange(value: number): void {
  if (adaptiveAudioRanges) {
    adaptiveAudioRanges = false;
    localStorage.setItem('gspace-adaptive-audio-ranges', 'false');
  }
  audioRange = Math.round(THREE.MathUtils.clamp(value, 1, 100));
  localStorage.setItem('gspace-audio-range', String(audioRange));
  updateUi();
}

function setAudioResponse(value: number): void {
  audioResponse = Math.round(THREE.MathUtils.clamp(value, 0.05, 2) * 20) / 20;
  localStorage.setItem('gspace-audio-response', String(audioResponse));
  sonifier.setResponse(audioResponse);
  updateUi();
}

function setMusicalMovement(value: number): void {
  musicalMovement = Math.round(THREE.MathUtils.clamp(value, 0, 1) * 100) / 100;
  localStorage.setItem('gspace-musical-movement', String(musicalMovement));
  sonifier.setMusicalMovement(musicalMovement);
  updateUi();
}

function setEventDensity(value: number): void {
  eventDensity = Math.round(THREE.MathUtils.clamp(value, 0, 1) * 100) / 100;
  localStorage.setItem('gspace-event-density', String(eventDensity));
  sonifier.setEventDensity(eventDensity);
  updateUi();
}

function setAdaptiveAudioRanges(enabled: boolean): void {
  adaptiveAudioRanges = enabled;
  localStorage.setItem('gspace-adaptive-audio-ranges', String(enabled));
  updateUi();
}

function setRareAccents(enabled: boolean): void {
  rareAccentsEnabled = enabled;
  localStorage.setItem('gspace-rare-accents', String(enabled));
  sonifier.setRareAccentsEnabled(enabled);
  updateUi();
}

function setSpatialSources(enabled: boolean): void {
  spatialSourcesEnabled = enabled;
  localStorage.setItem('gspace-spatial-sources', String(enabled));
  sonifier.setSpatialSourcesEnabled(enabled);
  updateUi();
}

function setDistanceAudioEnabled(enabled: boolean): void {
  distanceAudioEnabled = enabled;
  localStorage.setItem('gspace-distance-audio-enabled', String(enabled));
  sonifier.setDistanceEnabled(enabled);
  updateUi();
}

function setDistanceAudioMix(value: number): void {
  distanceAudioMix = Math.round(THREE.MathUtils.clamp(value, 0, 1) * 100) / 100;
  localStorage.setItem('gspace-distance-audio-mix', String(distanceAudioMix));
  sonifier.setDistanceMix(distanceAudioMix);
  updateUi();
}

function setDistanceAudioRange(value: number): void {
  if (adaptiveAudioRanges) {
    adaptiveAudioRanges = false;
    localStorage.setItem('gspace-adaptive-audio-ranges', 'false');
  }
  distanceAudioRange = Math.round(THREE.MathUtils.clamp(value, 10, 50000) / 10) * 10;
  localStorage.setItem('gspace-distance-audio-range', String(distanceAudioRange));
  updateUi();
}

function setCullEnabled(enabled: boolean): void {
  cullEnabled = enabled;
  simulation.setCulling(cullEnabled, cullDistance);
  updateUi();
}

function setCullDistance(value: number): void {
  cullDistance = Math.round(THREE.MathUtils.clamp(value, 100, 50000) / 100) * 100;
  simulation.setCulling(cullEnabled, cullDistance);
  updateUi();
}

function cycleQuality(): void {
  const order: QualityName[] = ['performance', 'balanced', 'quality'];
  setQuality(order[(order.indexOf(quality) + 1) % order.length]);
}

function adaptQuality(fps: number): void {
  if (!renderer.xr.isPresenting || quality === 'performance') { lowFpsSamples = 0; return; }
  const session = renderer.xr.getSession() as (XRSession & { frameRate?: number }) | null;
  const target = session?.frameRate ?? 72;
  lowFpsSamples = fps < target * 0.78 ? lowFpsSamples + 1 : 0;
  if (lowFpsSamples < 4) return;
  lowFpsSamples = 0;
  setQuality(quality === 'quality' ? 'balanced' : 'performance');
  toast('Quality adjusted to keep motion comfortable');
}

function togglePause(): void {
  simulation.paused = !simulation.paused;
  updateUi();
}

function restart(): void {
  simulation.rebuild(PRESETS[presetIndex], quality, particleGridSize);
  simulation.setColourScale(velocityColourScale);
  simulation.setPointScale(particlePointScale);
  simulation.setCulling(cullEnabled, cullDistance);
  simulation.root.rotation.set(0, 0, 0);
  updateUi();
  toast('Simulation restarted');
}

function resetView(): void {
  camera.position.set(0, 1.55, 3.1);
  controls.target.set(0, 1.55, -2.6);
  controls.update();
}

function resetCameraForPreset(): void {
  playerRig.position.set(0, 0, 0);
  playerRig.updateMatrixWorld(true);
  if (renderer.xr.isPresenting) {
    if (panel.visible) panelNeedsPlacement = true;
  } else {
    resetView();
  }
}

function updateUi(): void {
  presetSelect.value = String(presetIndex);
  qualitySelect.value = quality;
  particleGridInput.value = String(particleGridSize);
  particleCountOutput.textContent = formatParticleCount(particleGridSize);
  colourScaleInput.value = String(velocityColourScale);
  colourScaleOutput.textContent = String(velocityColourScale);
  pointScaleInput.value = String(particlePointScale);
  pointScaleOutput.textContent = String(particlePointScale);
  animationSpeedInput.value = String(animationSpeed);
  structureScaleInput.value = String(structureScaleFraction(structureScale));
  structureScaleOutput.textContent = `${formatAnimationSpeed(structureScale)}\u00d7`;
  distantBrightnessInput.value = String(distantBrightness);
  distantBrightnessOutput.textContent = `${formatAnimationSpeed(distantBrightness)}\u00d7`;
  passthroughInput.checked = passthroughEnabled;
  passthroughInput.disabled = !immersiveArSupported;
  passthroughOutput.textContent = immersiveArSupported ? (passthroughEnabled ? 'On' : 'Off') : 'Unavailable';
  const recordingActive = isVideoRecording();
  recordVideoButton.disabled = recordingFinalizing || (!recordingSupported && !passthroughSessionActive);
  recordVideoButton.textContent = recordingActive
    ? `Stop recording (${formatRecordingDuration(recordingElapsedSeconds)})`
    : passthroughSessionActive ? 'Quest recording help' : recordingFinalizing ? 'Preparing video…' : 'Start recording';
  recordingStatus.textContent = recordingActive
    ? `Recording ${formatRecordingDuration(recordingElapsedSeconds)}${sonifier.getRecordingAudioTrack() ? ' with audio' : ''}`
    : recordingNotice || (passthroughSessionActive
      ? 'Use Quest system Camera → Record video to include your room.'
      : recordingSupported ? 'Ready to record the virtual scene.' : 'Video recording is unavailable in this browser.');
  tracerTrailsInput.checked = tracerTrailsEnabled;
  tracerTrailsOutput.textContent = tracerTrailsEnabled ? 'On' : 'Off';
  gravityMarkersInput.checked = gravityMarkersEnabled;
  gravityMarkersOutput.textContent = gravityMarkersEnabled ? 'On' : 'Off';
  audioEnabledInput.checked = audioEnabled;
  audioEnabledOutput.textContent = audioEnabled ? 'On' : 'Off';
  velocityAudioEnabledInput.checked = velocityAudioEnabled;
  velocityAudioEnabledOutput.textContent = velocityAudioEnabled ? 'On' : 'Off';
  audioModeSelect.value = audioMode;
  velocityTimbreSelect.value = velocityTimbre;
  distanceTimbreSelect.value = distanceTimbre;
  audioPaletteOutput.textContent = formatAudioPalette(audioPalette);
  audioVolumeInput.value = String(Math.round(audioVolume * 100));
  audioVolumeOutput.textContent = `${Math.round(audioVolume * 100)}%`;
  audioRangeInput.value = String(audioRange);
  audioRangeInput.disabled = adaptiveAudioRanges;
  audioRangeOutput.textContent = String(audioRange);
  audioResponseInput.value = String(audioResponse);
  audioResponseOutput.textContent = `${audioResponse.toFixed(2)} s`;
  musicalMovementInput.value = String(Math.round(musicalMovement * 100));
  musicalMovementOutput.textContent = `${Math.round(musicalMovement * 100)}%`;
  eventDensityInput.value = String(Math.round(eventDensity * 100));
  eventDensityOutput.textContent = `${Math.round(eventDensity * 100)}%`;
  adaptiveAudioRangesInput.checked = adaptiveAudioRanges;
  adaptiveAudioRangesOutput.textContent = adaptiveAudioRanges ? 'On' : 'Off';
  rareAccentsInput.checked = rareAccentsEnabled;
  rareAccentsOutput.textContent = rareAccentsEnabled ? 'On' : 'Off';
  spatialSourcesInput.checked = spatialSourcesEnabled;
  spatialSourcesOutput.textContent = spatialSourcesEnabled ? 'On' : 'Off';
  distanceAudioEnabledInput.checked = distanceAudioEnabled;
  distanceAudioEnabledOutput.textContent = distanceAudioEnabled ? 'On' : 'Off';
  distanceAudioMixInput.value = String(Math.round(distanceAudioMix * 100));
  distanceAudioMixOutput.textContent = `${Math.round(distanceAudioMix * 100)}%`;
  distanceAudioRangeInput.value = String(distanceRangeFraction(distanceAudioRange));
  distanceAudioRangeInput.disabled = adaptiveAudioRanges;
  distanceAudioRangeOutput.textContent = distanceAudioRange.toLocaleString();
  animationSpeedOutput.textContent = `${formatAnimationSpeed(animationSpeed)}×`;
  cullEnabledInput.checked = cullEnabled;
  cullEnabledOutput.textContent = cullEnabled ? 'On' : 'Off';
  cullDistanceInput.value = String(cullDistance);
  cullDistanceInput.disabled = !cullEnabled;
  cullDistanceOutput.textContent = cullDistance.toLocaleString();
  pauseButton.textContent = simulation.paused ? 'Resume' : 'Pause';
  panel.update({
    preset: PRESETS[presetIndex].name,
    variableVelocity: hasVariableVelocity(PRESETS[presetIndex]),
    legacyMotionTuned: PRESETS[presetIndex].legacyMotionTuned,
    quality: QUALITY[quality].label,
    paused: simulation.paused,
    particleGrid: particleGridSize,
    colourScale: velocityColourScale,
    pointScale: particlePointScale,
    animationSpeed,
    structureScale,
    distantBrightness,
    passthroughEnabled,
    passthroughSupported: immersiveArSupported,
    passthroughActive: passthroughSessionActive,
    recordingSupported,
    recordingActive,
    recordingElapsedSeconds,
    recordingNotice,
    tracerTrailsEnabled,
    gravityMarkersEnabled,
    audioEnabled,
    velocityAudioEnabled,
    audioMode,
    velocityTimbre,
    distanceTimbre,
    audioPalette: formatAudioPalette(audioPalette),
    audioVolume,
    audioRange,
    audioResponse,
    musicalMovement,
    eventDensity,
    adaptiveAudioRanges,
    rareAccentsEnabled,
    spatialSourcesEnabled,
    distanceAudioEnabled,
    distanceAudioMix,
    distanceAudioRange,
    cullEnabled,
    cullDistance
  });
}

function clampParticleGrid(value: number): number {
  return Math.round(THREE.MathUtils.clamp(value, 64, 1224));
}

function formatParticleCount(grid: number): string {
  return (grid * grid).toLocaleString();
}

function presetCode(index: number): string {
  let value = index + 1;
  let code = '';
  while (value > 0) {
    value--;
    code = String.fromCharCode(65 + value % 26) + code;
    value = Math.floor(value / 26);
  }
  return code;
}

function formatAnimationSpeed(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2).replace(/0$/, '');
}

function formatRecordingDuration(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, '0')}`;
}

function structureScaleFromFraction(fraction: number): number {
  return 0.5 * Math.pow(80, THREE.MathUtils.clamp(fraction, 0, 1));
}

function structureScaleFraction(value: number): number {
  return Math.log(THREE.MathUtils.clamp(value, 0.5, 40) / 0.5) / Math.log(80);
}

function distanceRangeFromFraction(fraction: number): number {
  return 10 * Math.pow(5000, THREE.MathUtils.clamp(fraction, 0, 1));
}

function distanceRangeFraction(value: number): number {
  return Math.log(THREE.MathUtils.clamp(value, 10, 50000) / 10) / Math.log(5000);
}

function adaptAudioRangesFromHistograms(velocity: Float32Array, distance: Float32Array, time: number): void {
  if (!adaptiveAudioRanges) return;
  const velocityMean = histogramMeanBand(velocity);
  const distanceMean = histogramMeanBand(distance);
  const velocityError = Math.abs(velocityMean - 3.35) > 0.35 ? velocityMean - 3.35 : 0;
  const distanceError = Math.abs(distanceMean - 3.35) > 0.35 ? distanceMean - 3.35 : 0;
  audioRange = Math.round(THREE.MathUtils.clamp(audioRange * Math.exp(velocityError * 0.006), 1, 100) * 10) / 10;
  distanceAudioRange = Math.round(
    THREE.MathUtils.clamp(distanceAudioRange * Math.exp(distanceError * 0.006), 10, 50000) / 10
  ) * 10;
  if (time - lastAdaptiveAudioUiTime < 1000) return;
  lastAdaptiveAudioUiTime = time;
  localStorage.setItem('gspace-audio-range', String(audioRange));
  localStorage.setItem('gspace-distance-audio-range', String(distanceAudioRange));
  updateUi();
}

function histogramMeanBand(histogram: Float32Array): number {
  let mean = 0;
  for (let index = 0; index < histogram.length; index++) mean += histogram[index] * index;
  return mean;
}

function updateAudioSpatialization(): void {
  const viewCamera = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
  viewCamera.getWorldPosition(audioHeadPosition);
  viewCamera.getWorldDirection(audioViewDirection).normalize();
  audioRight.copy(audioViewDirection).cross(worldUp).normalize();
  const pans = simulation.getGravitySourceWorldPositions().map((source) => {
    audioSourceDirection.copy(source).sub(audioHeadPosition);
    if (audioSourceDirection.lengthSq() < 0.0001) return 0;
    return THREE.MathUtils.clamp(audioSourceDirection.normalize().dot(audioRight) * 1.25, -0.9, 0.9);
  });
  sonifier.setGravitySourcePans(pans);
}

function paletteForPreset(preset: Preset): AudioPalette {
  if (preset.velocityMode === 'keplerian' || preset.velocityMode === 'binaryLagrange'
    || /orbit|binary/i.test(preset.name)) return 'orbital';
  if (preset.velocityMode === 'tidalFlyby' || preset.velocityMode === 'cloudCollision'
    || /encounter|passage|wanderer|rapid/i.test(preset.name)) return 'tense';
  if (preset.velocityMode === 'rotatingCollapse' || preset.particleShape === 'solidSphere'
    || /cluster/i.test(preset.name)) return 'deep';
  return 'luminous';
}

function formatAudioPalette(palette: AudioPalette): string {
  return palette.charAt(0).toUpperCase() + palette.slice(1);
}

function presetCullDistance(index: number): number {
  return Math.min(Math.max(PRESETS[index].radius * 8, 6000), 50000);
}

let toastTimer = 0;
function toast(message: string): void {
  const element = required<HTMLElement>('#toast');
  element.textContent = message;
  element.classList.add('visible');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => element.classList.remove('visible'), 2200);
}

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

function showFatal(message: string): void {
  const app = document.querySelector('#app');
  if (app) app.innerHTML = `<div style="padding:3rem;font-family:system-ui"><h1>GSpace could not start</h1><p>${message}</p></div>`;
}
